import { formatDate } from '@thoughtspot/date-format-util';
import { useTranslation } from '@thoughtspot/i18n';
import { BannerAlertType } from '@thoughtspot/radiant-react/widgets/alert';
import React, { useEffect, useMemo, useState } from 'react';
import { useDeleteSslMutation, useGetSslQuery, useSetSsl, } from "../../../../services/system/system-service";
import { Content, Seperator } from '../../common';
import { ActionButtons, LearnMore, Mode, TranslationsKeys as TranslationsKeysCommon, } from '../../common/constants';
import { getConfig } from '../../common/form-builder-config';
import { getFormFields, parseFormData } from '../../common/utils';
import { TranslationsKeys } from '../constants';
import styles from '../security.module.scss';
const DATE_FORMAT_STRING = 'EEEE, d MMMM yyyy, t ZZZZ';
export const SslComponent = ({ pageName, mode, setMode, formType, setFormType, showLoader, hideLoader, onSuccess, onError, }) => {
    const { t } = useTranslation();
    const InitialSslConfig = useMemo(() => getConfig('ssl'), []);
    const [sslData, setSslData] = useState({});
    const [sslFormFields, setSslFormFields] = useState([]);
    const [sslPostData, setSslPostData] = useState(null);
    const [postSubmitError, setPostSubmitError] = useState('');
    const [bothUpload, setBothUpload] = useState(false);
    const { data: sslResponse, loading: isSslLoading, error: sslError, } = useGetSslQuery();
    const { useSetSslMutation, data: setSslDataResponse, loading: isSetSslLoading, error: setSslError, } = useSetSsl();
    const getFieldConfig = (field) => {
        switch (field.key) {
            case 'ssl_status':
                return {
                    label: field.config.label,
                    isTextValueColorLight: !sslData.is_configured,
                };
            case 'signing_algorithm':
                return {
                    label: field.config.label,
                    placeholder: t(TranslationsKeys.SSL_SELECT_INPUT_PLACEHOLDER, {
                        fieldName: t(TranslationsKeysCommon.SIGNING_ALGORITHM),
                    }),
                    options: [
                        {
                            options: [
                                {
                                    id: 'RSA',
                                    label: 'RSA',
                                },
                            ],
                        },
                    ],
                    dropdownWidth: field.config.dropdownWidth,
                };
            case 'tls_version':
                return {
                    label: field.config.label,
                    placeholder: t(TranslationsKeys.SSL_SELECT_INPUT_PLACEHOLDER, { fieldName: t(TranslationsKeysCommon.TLS_VERSION) }),
                    options: [
                        {
                            options: [
                                {
                                    id: '1.2',
                                    label: '1.2',
                                },
                                {
                                    id: '1.1',
                                    label: '1.1',
                                },
                                {
                                    id: '1.0',
                                    label: '1.0',
                                },
                            ],
                        },
                    ],
                    dropdownWidth: field.config.dropdownWidth,
                };
            case 'key':
                return {
                    type: field.config.type,
                    label: field.config.label,
                    placeholder: field.config.placeholder,
                };
            case 'cert':
                return {
                    type: field.config.type,
                    label: field.config.label,
                    placeholder: field.config.placeholder,
                };
            default:
                return field.config;
        }
    };
    const getAdditionlFieldAttribute = (field) => {
        switch (field.key) {
            case 'ssl_status':
                if (!sslData.is_configured && mode === Mode.VIEW) {
                    return {
                        true: t(TranslationsKeys.UNCONFIGURED),
                        false: t(TranslationsKeys.UNCONFIGURED),
                    };
                }
                if (mode === Mode.EDIT) {
                    return {
                        true: t(TranslationsKeys.EDIT_FORM_ENABLE_LABEL),
                        false: t(TranslationsKeys.EDIT_FORM_DISABLE_LABEL),
                    };
                }
                return {
                    true: t(TranslationsKeys.SSL_TOGGLE_ENABLED_LABEL),
                    false: t(TranslationsKeys.SSL_TOGGLE_DISABLED_LABEL),
                };
            case 'key':
                if (sslData.is_configured && mode === Mode.EDIT) {
                    return 'test.key';
                }
                return '';
            case 'cert':
                if (sslData.is_configured && mode === Mode.EDIT) {
                    return 'test.cert';
                }
                return '';
            default:
                return null;
        }
    };
    const updateFormFields = (keyName, formData = {}, errorMessages = {}) => {
        const fieldsToShow = [];
        if (mode === Mode.VIEW && !formData.is_configured) {
            const configuredStatus = InitialSslConfig.find((config) => config.key === keyName);
            const newConfig = configuredStatus;
            newConfig.valueLabels = getAdditionlFieldAttribute(configuredStatus);
            fieldsToShow.push({
                ...configuredStatus,
                configCallback: getFieldConfig,
            });
        }
        else {
            InitialSslConfig.forEach((config) => {
                if (mode === Mode.VIEW && config.view) {
                    const newConfig = config;
                    newConfig.valueLabels = getAdditionlFieldAttribute(config);
                    fieldsToShow.push({
                        ...config,
                        configCallback: getFieldConfig,
                    });
                }
                else if (mode === Mode.EDIT && config.edit) {
                    const newConfig = config;
                    if (config.key === keyName) {
                        newConfig.valueLabels = getAdditionlFieldAttribute(config);
                    }
                    fieldsToShow.push({
                        ...config,
                        configCallback: getFieldConfig,
                    });
                }
            });
        }
        const newFormFields = getFormFields(formData, fieldsToShow, errorMessages, mode);
        setSslFormFields(newFormFields);
    };
    const parseSslData = (dataToParse = {}) => {
        let destructData = {};
        const { ssl_status, tls_version, is_configured } = dataToParse;
        destructData = {
            ssl_status,
            tls_version,
            is_configured,
            ...dataToParse.ssl_info,
        };
        destructData.not_after = formatDate(new Date(destructData.not_after), DATE_FORMAT_STRING);
        destructData.signing_algorithm =
            destructData.signing_algorithm === null
                ? 'RSA'
                : destructData.signing_algorithm;
        if (is_configured) {
            destructData.key = '****.key';
            destructData.cert = '****.cert';
        }
        return destructData;
    };
    const fetchSsl = () => {
        if (sslResponse && !isSslLoading && sslResponse.Data) {
            hideLoader();
            const parseData = parseSslData(sslResponse.Data);
            setSslData(parseData);
        }
        else if (sslResponse && !isSslLoading && sslResponse.Error) {
            onError(t(TranslationsKeys.MESSAGE_API_FAILURE));
        }
    };
    useEffect(() => {
        fetchSsl();
    }, [sslResponse]);
    useEffect(() => {
        updateFormFields('ssl_status', sslData);
    }, [sslData, mode]);
    const getCustomValidation = (field) => {
        switch (field.id) {
            case 'key':
                if (!field.file) {
                    return t(TranslationsKeysCommon.SSL_FILE_INPUT_INVALID_FILE_FORM_ERROR, { fieldName: t(TranslationsKeysCommon.PRIVATE_KEY) });
                }
                return false;
            case 'cert':
                if (!field.file) {
                    return t(TranslationsKeysCommon.SSL_FILE_INPUT_INVALID_FILE_FORM_ERROR, {
                        fieldName: t(TranslationsKeysCommon.SSL_CERTIFICATE_BUNDLE),
                    });
                }
                return false;
            default:
                return false;
        }
    };
    const validateForm = (currentFormData = {}, editedField = '') => {
        let anyInValid = false;
        const errorMessages = {};
        let customInValid = true;
        let isValid = false;
        currentFormData.forEach((i) => {
            if (i && i.valid === false) {
                anyInValid = true;
                return;
            }
        });
        if (!anyInValid) {
            if (!sslData.is_configured && editedField.trim() === '') {
                currentFormData.forEach((field) => {
                    const customErrorMessage = getCustomValidation(field);
                    if (customErrorMessage) {
                        customInValid = false;
                        errorMessages[field.id] = customErrorMessage;
                    }
                });
            }
            const isFileInputEdited = editedField === 'key' || editedField === 'cert';
            if (isFileInputEdited) {
                const field = currentFormData.filter((i) => i.id === editedField);
                const customErrorMessage = getCustomValidation(field);
                if (customErrorMessage) {
                    customInValid = false;
                    errorMessages[editedField] = customErrorMessage;
                }
            }
            if ((isFileInputEdited && sslData.is_configured) || bothUpload) {
                customInValid = false;
                currentFormData.forEach((field) => {
                    if (field.id === 'key' || field.id === 'cert') {
                        if (field.file) {
                            setBothUpload(false);
                        }
                        else {
                            setBothUpload(true);
                        }
                    }
                });
            }
            if (!customInValid) {
                isValid = false;
                const formData = parseFormData(currentFormData);
                updateFormFields('ssl_status', formData, errorMessages);
            }
            else {
                isValid = true;
                setBothUpload(false);
            }
        }
        return isValid;
    };
    const handleIsEdited = (currentFormData, id) => {
        validateForm(currentFormData, id);
    };
    const onSaveSuccess = (response) => {
        const parseData = parseSslData(response.Data);
        setSslData(parseData || {});
        setFormType('');
        setPostSubmitError('');
        setMode(Mode.VIEW);
        onSuccess(t(TranslationsKeys.TOAST_SUCCESS));
    };
    const onSave = (currentFormData) => {
        const isFormValid = validateForm(currentFormData);
        if (isFormValid) {
            const postParams = new FormData();
            currentFormData.forEach((formInput) => {
                switch (formInput.id) {
                    case 'ssl_status':
                        postParams.append('status', formInput.value ? 'enable' : 'disable');
                        break;
                    case 'signing_algorithm':
                        postParams.append('rsaAlgorithm', formInput.value);
                        break;
                    case 'tls_version':
                        postParams.append('tls', formInput.value);
                        break;
                    case 'key':
                        postParams.append('key', formInput.file);
                        break;
                    case 'cert':
                        postParams.append('cert', formInput.file);
                        break;
                    default:
                        break;
                }
            });
            setBothUpload(false);
            setSslPostData(postParams);
        }
    };
    useEffect(() => {
        if (setSslDataResponse && !isSetSslLoading) {
            const sslInfo = Object.keys(setSslDataResponse.Data.ssl_info)
                .length;
            if (sslInfo > 0) {
                setSslDataResponse.Data.is_configured = true;
            }
            else {
                setSslDataResponse.Data.is_configured = false;
            }
            onSaveSuccess(setSslDataResponse);
        }
    }, [setSslDataResponse, isSetSslLoading]);
    useEffect(() => {
        if (setSslError && !isSetSslLoading) {
            setPostSubmitError(setSslError.message);
            hideLoader();
        }
    }, [setSslError, isSetSslLoading]);
    const postSslData = async (sslPostFormData) => {
        try {
            const sslPayload = {
                status: sslPostFormData.get('status'),
                rsaAlgorithm: sslPostFormData.get('rsaAlgorithm'),
                tls: sslPostFormData.get('tls'),
                key: sslPostFormData.get('key'),
                cert: sslPostFormData.get('cert'),
            };
            const response = await useSetSslMutation(sslPayload);
            return response;
        }
        catch (error) {
            return error;
        }
    };
    useEffect(() => {
        if (sslPostData) {
            showLoader();
            postSslData(sslPostData);
        }
    }, [sslPostData]);
    const [deleteSsl] = useDeleteSslMutation(response => {
        const parseData = parseSslData(response.Data);
        setSslData(parseData || {});
        setFormType('');
        setMode(Mode.VIEW);
        onSuccess(t(TranslationsKeys.TOAST_DELETE_SUCCESS));
    }, onError);
    const actionHandler = (type, currentFormData = {}) => {
        switch (type) {
            case ActionButtons.EDIT:
                setFormType('ssl');
                setMode(Mode.EDIT);
                break;
            case ActionButtons.BACK:
            case ActionButtons.CANCEL:
                setMode(Mode.VIEW);
                break;
            case ActionButtons.SAVE:
                onSave(currentFormData);
                break;
            case ActionButtons.DELETE:
                showLoader();
                deleteSsl();
                break;
            default:
                setMode(Mode.EDIT);
                break;
        }
    };
    const renderContent = () => {
        const config = {
            actions: [
                {
                    type: ActionButtons.EDIT,
                    title: sslData.is_configured
                        ? t(TranslationsKeys.EDIT_BUTTON)
                        : t(TranslationsKeys.CONFIGURE_BUTTON),
                },
            ],
            title: t(TranslationsKeys.SSL_TITLE),
            subTitle: t(TranslationsKeys.SSL_SUBTITLE),
            learnMoreKey: LearnMore.SSL,
            formFields: sslFormFields,
        };
        const actions = {
            primary: {
                type: ActionButtons.SAVE,
                text: t(TranslationsKeys.SAVE_BUTTON),
                returnData: true,
            },
            secondary: {
                type: ActionButtons.CANCEL,
                text: t(TranslationsKeys.CANCEL_BUTTON),
            },
        };
        if (sslData.is_configured) {
            actions.link = {
                type: ActionButtons.DELETE,
                text: t(TranslationsKeys.DELETE_CONFIGURATION_LINK),
                askConfirm: true,
            };
        }
        const sslBannerAlertProps = {
            isVisible: mode === Mode.EDIT && bothUpload,
            type: BannerAlertType.ERROR,
            className: styles.sslBanner,
            message: t(TranslationsKeys.SSL_UPLOAD_BOTH_ERROR),
            showCloseButton: false,
            global: false,
        };
        const sslFormErrorBannerAlertProps = {
            isVisible: mode === Mode.EDIT && postSubmitError !== '',
            type: BannerAlertType.ERROR,
            className: styles.sslBanner,
            message: postSubmitError,
            showCloseButton: false,
            global: false,
        };
        const sslBanner = postSubmitError !== ''
            ? sslFormErrorBannerAlertProps
            : sslBannerAlertProps;
        return (React.createElement(Content, { key: `ssl_${mode}`, config: config, mode: mode, pageName: pageName, backConfirm: true, actions: actions, banner: sslBanner, actionHandler: actionHandler, onEdit: handleIsEdited }));
    };
    if (isSslLoading || !sslResponse) {
        showLoader();
    }
    return (React.createElement("div", { className: styles.securityContainer, "data-testid": "ssl-container" },
        mode === Mode.VIEW && React.createElement(Seperator, null),
        (mode === Mode.VIEW || formType === 'ssl') && renderContent()));
};
//# sourceMappingURL=ssl.container.js.map